import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { AssignmentStatusByStudentItem } from '../../../../constants/types/lms/recitation/type';
import TextWinds from '../../../common/TextWinds';
import { useMemo, useState } from 'react';
import { Input } from 'antd';

interface StudentListContentsProps {
  studentList: AssignmentStatusByStudentItem[];
  handleStudentClick: (classId: number) => void;
}

/**
 * 개편 LMS (교사) - AI인증제 관리 > AI인증제 현황 페이지 > 학생 목록
 */
const StudentListContents = ({
  studentList,
  handleStudentClick,
}: StudentListContentsProps) => {
  const [studentNameFilter, setStudentNameFilter] = useState('');

  const filteredStudentList = useMemo(() => {
    const keyword = studentNameFilter.trim().toLowerCase();

    if (!keyword) return studentList;

    return studentList.filter((studentItem) =>
      studentItem.name.toLowerCase().includes(keyword),
    );
  }, [studentList, studentNameFilter]);

  return (
    <>
      <div className="mb-5 flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0">
          <TextWinds type="title_h3">확인할 학생을 선택해주세요.</TextWinds>

          <TextWinds type="caption1" color="gray8" className="mt-1 block">
            학생을 선택하면 해당 학생의 과제 진행 현황을 확인할 수 있습니다.
          </TextWinds>
        </div>

        <Input
          type="search"
          allowClear
          className="w-full xl:w-[240px]"
          placeholder="학생명 검색"
          value={studentNameFilter}
          onChange={(event) => setStudentNameFilter(event.target.value)}
        />
      </div>

      <div className="flex flex-col gap-3">
        {filteredStudentList.length === 0 ? (
          <div className="flex h-[240px] items-center justify-center rounded-xl border border-gray4 bg-gray0 px-4 text-center">
            <TextWinds type="content_body2" color="gray6">
              조회된 학생 목록이 없습니다.
            </TextWinds>
          </div>
        ) : (
          <>
            {filteredStudentList.map((studentItem) => {
              const progressColorClassName =
                studentItem.progressRate <= 0
                  ? 'bg-gray3'
                  : studentItem.progressRate >= 100
                    ? 'bg-green-500'
                    : 'bg-[#0B8ED0]';

              return (
                <button
                  key={studentItem.id}
                  type="button"
                  className="group flex w-full items-center gap-4 rounded-xl border border-gray3 bg-white px-4 py-4 text-left transition-all duration-200 hover:border-purple5 hover:bg-purple1 hover:shadow-sm active:scale-[0.99]"
                  onClick={() => handleStudentClick(studentItem.id)}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <TextWinds
                          type="content_body2"
                          color="gray9"
                          className="font-bold group-hover:text-purple5"
                        >
                          {studentItem.name}
                        </TextWinds>

                        <TextWinds type="caption1" color="gray6">
                          {studentItem.uid}
                        </TextWinds>

                        {studentItem.classGroupName && (
                          <div className="flex items-center rounded-full border border-purple2 bg-purple1 px-2.5 py-0.5">
                            <TextWinds
                              type="caption1"
                              className="font-bold text-purple5"
                            >
                              {studentItem.classGroupName}
                            </TextWinds>
                          </div>
                        )}
                      </div>
                    </div>

                    <TextWinds
                      type="caption1"
                      color="gray8"
                      className="mt-0.5 block"
                    >
                      과제 {studentItem.todoCount}개 ·&nbsp;
                      {studentItem.completedCount}개 완료
                    </TextWinds>

                    <div className="mt-3 flex items-center gap-3">
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray1">
                        <div
                          className={[
                            'h-full rounded-full transition-all duration-300',
                            progressColorClassName,
                          ].join(' ')}
                          style={{ width: `${studentItem.progressRate}%` }}
                        />
                      </div>

                      <TextWinds
                        type="caption1"
                        color="gray8"
                        className="hidden shrink-0 font-bold xl:block"
                      >
                        {studentItem.progressRate}%
                      </TextWinds>
                    </div>
                  </div>

                  <ChevronRightIcon className="h-5 w-5 shrink-0 text-gray6 group-hover:text-purple5" />
                </button>
              );
            })}
          </>
        )}
      </div>
    </>
  );
};

export default StudentListContents;
